/**
 * CrmGoals - Página de gerenciamento de metas de vendedores CRM
 * 
 * Funcionalidades:
 * - Listagem de metas com busca e filtros
 * - Criar, editar e excluir metas
 * - KPIs com estatísticas das metas
 * - Visualização de progresso por meta
 * - Filtros por vendedor, tipo e período
 * 
 * @version 1.0.0 - Layout padronizado com CrmSellers/CrmCustomers
 * @author Sistema
 */

import React, { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';
import {
  Plus,
  Search,
  Target,
  TrendingUp,
  CheckCircle2,
  Percent,
  X,
  Calendar,
} from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SellerSelector } from '@/components/crm/SellerSelector';
import { WorkspaceMultiSelect } from '@/components/crm/WorkspaceMultiSelect';
import { GoalFormDialog, GoalsTable } from '@/components/crm/goals';
import { useSellerGoals, SellerGoal, GoalFilters } from '@/hooks/useSellerGoals';

// ============================================================================
// INTERFACES - Definição de tipos para estatísticas
// ============================================================================

/** Interface para estatísticas de metas (KPIs) */
interface GoalStats {
  total_count: number;
  current_month_count: number;
  achieved_count: number;
  avg_achievement: number;
}

// ============================================================================
// COMPONENTES AUXILIARES - Cards de métricas (padrão Clientes)
// ============================================================================

/** Props do componente MetricCard */
interface MetricCardProps {
  title: string;
  value: number;
  icon: LucideIcon;
  variant?: 'primary' | 'success' | 'info' | 'purple';
  suffix?: string;
}

/**
 * Estilos de variantes para KPI cards - padrão horizontal compacto
 */
const METRIC_CARD_STYLES: Record<string, { cardBg: string; iconColor: string }> = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  purple: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
};

/**
 * Card de métrica compacto horizontal - padrão unificado CRM
 */
function MetricCard({ title, value, icon: Icon, variant = 'primary', suffix }: MetricCardProps) {
  const styles = METRIC_CARD_STYLES[variant] || METRIC_CARD_STYLES.primary;

  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">
            {value}{suffix}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL - Gerenciamento de metas do CRM
// ============================================================================

/**
 * Página principal de metas - Layout padronizado com Clientes/Vendedores
 */
export default function CrmGoals() {
  // Estados de filtro
  const [search, setSearch] = useState('');
  const [sellerFilter, setSellerFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<'all' | 'revenue' | 'deals_count'>('all');
  const [workspaceFilter, setWorkspaceFilter] = useState<string[]>([]);
  
  // Estados de dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<SellerGoal | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState<SellerGoal | null>(null);

  // Filtros para o hook (sem filtro de busca textual - aplicado localmente)
  const filters: GoalFilters = useMemo(() => ({
    seller_id: sellerFilter || null,
    goal_type: typeFilter !== 'all' ? typeFilter : null,
    workspace_id: workspaceFilter.length === 1 ? workspaceFilter[0] : null,
  }), [sellerFilter, typeFilter, workspaceFilter]);

  // Hook de metas
  const { goals, isLoading, deleteGoal } = useSellerGoals(filters);

  // ============================================================================
  // COMPUTED - Estatísticas calculadas a partir dos dados
  // ============================================================================

  /** Metas filtradas pela busca textual (nome do vendedor) e workspaces */
  const filteredGoals = useMemo(() => {
    let result = goals;
    
    // Filtro por busca textual
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(goal =>
        goal.seller_name.toLowerCase().includes(searchLower) ||
        goal.notes?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filtro por múltiplos workspaces (quando > 1 selecionado)
    if (workspaceFilter.length > 1) {
      result = result.filter(goal =>
        goal.workspace_ids?.some(wsId => workspaceFilter.includes(wsId)) ||
        (goal.workspace_ids?.length === 0) // Metas sem workspace aparecem em todos
      );
    }
    
    return result;
  }, [goals, search, workspaceFilter]);

  /** Calcula estatísticas/KPIs das metas */
  const stats = useMemo<GoalStats>(() => {
    const now = new Date();
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);

    // Metas do mês atual
    const currentMonthGoals = goals.filter(g => {
      const start = new Date(g.period_start);
      const end = new Date(g.period_end);
      return start <= monthEnd && end >= monthStart;
    });

    // Metas atingidas (>= 100%)
    const achievedCount = goals.filter(g => g.achievement_percentage >= 100).length;

    // Média de atingimento
    const avgAchievement = goals.length > 0
      ? goals.reduce((acc, g) => acc + g.achievement_percentage, 0) / goals.length
      : 0;

    return {
      total_count: goals.length,
      current_month_count: currentMonthGoals.length,
      achieved_count: achievedCount,
      avg_achievement: Math.round(avgAchievement * 10) / 10,
    };
  }, [goals]);

  /** Verifica se há filtros ativos */
  const hasActiveFilters = search || sellerFilter || typeFilter !== 'all' || workspaceFilter.length > 0;

  /** Limpa todos os filtros */
  const clearFilters = () => {
    setSearch('');
    setSellerFilter(null);
    setTypeFilter('all');
    setWorkspaceFilter([]);
  };

  // ============================================================================
  // HANDLERS - Ações do usuário
  // ============================================================================

  /** Handler para abrir edição */
  const handleEdit = (goal: SellerGoal) => {
    setEditingGoal(goal);
    setFormOpen(true);
  };

  /** Handler para abrir confirmação de exclusão */
  const handleDeleteClick = (goal: SellerGoal) => {
    setGoalToDelete(goal);
    setDeleteDialogOpen(true);
  };

  /** Handler para confirmar exclusão */
  const handleDeleteConfirm = async () => {
    if (goalToDelete) {
      await deleteGoal.mutateAsync(goalToDelete.id);
      setDeleteDialogOpen(false);
      setGoalToDelete(null);
    }
  };

  /** Handler para fechar dialog de formulário */
  const handleFormClose = () => {
    setFormOpen(false);
    setEditingGoal(null);
  };

  /** Handler para sucesso no formulário */
  const handleFormSuccess = () => {
    handleFormClose();
  };

  // ============================================================================
  // RENDER - Interface principal (Layout padrão Clientes)
  // ============================================================================

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto - Padrão Clientes */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Metas</h1>
        </div>
        <Button
          size="xs"
          onClick={() => setFormOpen(true)}
          className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Nova Meta
        </Button>
      </div>

      {/* KPI Cards em Grid - Padrão Clientes */}
      {!isLoading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
          <MetricCard
            title="Total de Metas"
            value={stats.total_count}
            icon={Target}
            variant="primary"
          />
          <MetricCard
            title="Metas do Mês"
            value={stats.current_month_count}
            icon={Calendar}
            variant="info"
          />
          <MetricCard
            title="Metas Atingidas"
            value={stats.achieved_count}
            icon={CheckCircle2}
            variant="success"
          />
          <MetricCard
            title="Média de Atingimento"
            value={stats.avg_achievement}
            icon={Percent}
            variant="purple"
            suffix="%"
          />
        </div>
      )}

      {/* Barra de Filtros Inline - Padrão Clientes */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30">
        {/* Busca */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por vendedor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>

        {/* Filtro Vendedor */}
        <div className="w-[200px]">
          <SellerSelector
            value={sellerFilter || undefined}
            onSelect={(id) => setSellerFilter(id || null)}
            placeholder="Todos vendedores"
            allowClear
            className="h-8"
          />
        </div>

        {/* Filtro Tipo */}
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as 'all' | 'revenue' | 'deals_count')}>
          <SelectTrigger className="w-[140px] h-8 text-sm">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            <SelectItem value="revenue">Faturamento</SelectItem>
            <SelectItem value="deals_count">Negócios</SelectItem>
          </SelectContent>
        </Select>

        {/* Filtro Workspace */}
        <div className="w-[200px]">
          <WorkspaceMultiSelect
            value={workspaceFilter}
            onChange={setWorkspaceFilter}
            placeholder="Todos workspaces"
          />
        </div>

        {/* Botão Limpar */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-2 text-xs">
            <X className="h-3 w-3 mr-1" />
            Limpar
          </Button>
        )}

        {/* Indicador carregamento */}
        {isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse">
            Atualizando...
          </span>
        )}
      </div>

      {/* Container da Tabela */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : filteredGoals.length === 0 ? (
          /* Estado vazio compacto - Padrão Clientes */
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Target className="h-12 w-12 text-muted-foreground/40" />
            <div className="text-center">
              <h3 className="font-medium text-sm">
                {hasActiveFilters ? 'Nenhuma meta encontrada' : 'Nenhuma meta cadastrada'}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {hasActiveFilters ? 'Tente ajustar os filtros' : 'Cadastre a primeira meta da equipe'}
              </p>
            </div>
            {!hasActiveFilters && (
              <Button size="xs" onClick={() => setFormOpen(true)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Nova Meta
              </Button>
            )}
          </div>
        ) : (
          <GoalsTable
            goals={filteredGoals}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
          />
        )}
      </div>

      {/* Dialog de Formulário */}
      <GoalFormDialog
        open={formOpen}
        onOpenChange={handleFormClose}
        goal={editingGoal}
        onSuccess={handleFormSuccess}
      />

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir meta?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a meta de "{goalToDelete?.seller_name}"
              {goalToDelete && ` (${goalToDelete.goal_type === 'revenue' ? 'Faturamento' : 'Negócios'})`}?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
